import { Fragment } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The shared wizard step indicator: numbered circles joined by connectors,
 * ticking over to a check once passed.
 *
 * Three import wizards (CSV readings, ESPM, CAMT) each hand-rolled this same
 * markup, which is why it is a primitive rather than a fourth copy.
 *
 * ── ONE TONE, AND WHY ───────────────────────────────────────────────────────
 * It lives on `FullscreenDialogHeader`, a REGISTERED THEMABLE SURFACE
 * (docs/THEMING.md): the band paints `bg-dialog-header text-dialog-header-foreground`
 * and both flip per preset. On `calm` the band is LIGHT with dark ink, so the
 * hardcoded `text-white` / `bg-white text-releve-teal-dark` that the CAMT copy
 * carried was invisible there. Every colour below is therefore a
 * `dialog-header-*` token and there is deliberately no second tone: a variant
 * with raw colours would reintroduce exactly that bug.
 *
 * ── PURELY PRESENTATIONAL ───────────────────────────────────────────────────
 * No navigation, no click handlers — none of the three copies had any, and a
 * step strip that looks clickable but is not is worse than one that does not.
 *
 * ── NO i18n INSIDE ──────────────────────────────────────────────────────────
 * `label` and `ariaLabel` arrive already translated, so the primitive stays
 * domain-agnostic (CLAUDE.md: generic primitives take no domain types).
 *
 * ── THE CALLER FILTERS ──────────────────────────────────────────────────────
 * Wizards whose step list varies (CAMT drops two steps for some imports) pass
 * the ALREADY-FILTERED array. Deliberately no `hidden` flag per step: the only
 * test guarding CAMT's filtering greps label text, so a hidden-flag design
 * would let that regress silently while the test still passed.
 *
 * ── RESPONSIVE ──────────────────────────────────────────────────────────────
 * Labels hide below `lg` and the whole strip below `md`, matching the copies it
 * replaces. So it is ORIENTATION, NOT CONTENT — callers must not put anything
 * here that the dialog body does not also say. Note the labels are always in
 * the DOM: hiding them is a CSS concern, never a JS one.
 */
export interface StepIndicatorStep {
  /** Stable key — also the React key. */
  key: string;
  /** Already-translated label. This primitive does no i18n of its own. */
  label: string;
}

export interface StepIndicatorProps {
  /** Already-filtered, in display order. */
  steps: StepIndicatorStep[];
  /** Zero-based index of the step in progress. */
  currentIndex: number;
  /** Accessible name for the list, e.g. "Import progress". Already translated. */
  ariaLabel: string;
  className?: string;
}

export function StepIndicator({
  steps,
  currentIndex,
  ariaLabel,
  className,
}: Readonly<StepIndicatorProps>) {
  return (
    <ol
      aria-label={ariaLabel}
      className={cn('hidden md:flex items-center gap-2', className)}
      data-testid="step-indicator"
    >
      {steps.map((step, index) => {
        const isPast = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <Fragment key={step.key}>
            {index > 0 && (
              <li
                aria-hidden="true"
                className={cn(
                  'w-8 h-px',
                  index <= currentIndex
                    ? 'bg-dialog-header-foreground'
                    : 'bg-dialog-header-foreground/30',
                )}
              />
            )}
            <li
              // `aria-current="step"` is the whole accessible story here: the
              // circle's number and the tick are decorative, and the label is
              // hidden below `lg`.
              aria-current={isCurrent ? 'step' : undefined}
              data-state={isPast ? 'complete' : isCurrent ? 'current' : 'upcoming'}
              className={cn(
                'flex items-center gap-1.5 text-sm',
                isCurrent
                  ? 'text-dialog-header-foreground font-medium'
                  : isPast
                    ? 'text-dialog-header-foreground/80'
                    : 'text-dialog-header-foreground/40',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0',
                  isPast
                    ? 'bg-dialog-header-foreground text-dialog-header'
                    : isCurrent
                      ? 'bg-dialog-header-foreground/20 border border-dialog-header-foreground'
                      : 'bg-dialog-header-foreground/10 border border-dialog-header-foreground/30',
                )}
              >
                {isPast ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              <span className="hidden lg:inline">{step.label}</span>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}
